'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowRight, Eye, EyeOff, Loader2 } from 'lucide-react';

import { AuthFormAlerts } from '@/components/auth/AuthFormAlerts';
import { BrandMark } from '@/components/BrandMark';

import { api, ApiError } from '@/lib/api';
import { type AuthFormAlert, parseAuthFormAlert, translateAuthApiError, translateAuthError } from '@/lib/auth-errors';
import { useStore } from '@/lib/store';
import { isWebAdminEligible } from '@/lib/access';
import {
  authCardClass,
  authFieldClass,
  authIconButtonClass,
  authLabelClass,
  authMutedTextClass,
  authPageShellClass,
  authPanelClass,
} from '@/lib/auth-page-styles';

export default function LoginPage() {
  const router = useRouter();
  const setUser = useStore((state) => state.setUser);
  const setBootstrap = useStore((state) => state.setBootstrap);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [alert, setAlert] = useState<AuthFormAlert | null>(null);
  const [apiOk, setApiOk] = useState<boolean | null>(null);
  const [registered, setRegistered] = useState(false);
  const [needsEmailVerify, setNeedsEmailVerify] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [pwaOnlyHint, setPwaOnlyHint] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  useEffect(() => {
    fetch('/health', { method: 'GET' })
      .then((r) => r.ok)
      .then(setApiOk)
      .catch(() => setApiOk(false));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const emailFromQuery = params.get('email');
    if (emailFromQuery) {
      setEmail(emailFromQuery);
    }
    setRegistered(params.get('registered') === '1');
    setPwaOnlyHint(params.get('reason') === 'pwa_only');
  }, []);

  const onSuccess = (user: any, bootstrap?: any) => {
    if (bootstrap) {
      setBootstrap(bootstrap);
    } else {
      setUser(user);
    }
    sessionStorage.setItem('auth_just_logged_in', '1');
    setSuccess(true);
    router.replace('/');
    window.location.replace('/');
    window.setTimeout(() => {
      if (window.location.pathname === '/login') {
        window.location.assign('/');
      }
    }, 250);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAlert(null);
    setNeedsEmailVerify(false);
    setLoading(true);
    try {
      const { user, bootstrap } = await api.login(email, password);
      if (!isWebAdminEligible(user)) {
        setAlert({
          message:
            'Доступ в админ-панель только для администратора или менеджера. Сотрудники входят в приложение (PWA).',
        });
        api.logout();
        return;
      }
      onSuccess(user, bootstrap);
    } catch (err: unknown) {
      const detailRaw = err instanceof ApiError ? err.data?.detail : (err as { data?: { detail?: unknown } })?.data?.detail;
      const detailStr =
        typeof detailRaw === 'string'
          ? detailRaw
          : Array.isArray(detailRaw)
            ? (detailRaw[0] as { msg?: string })?.msg
            : undefined;
      if (err instanceof ApiError && err.status === 403 && detailStr === 'email_not_verified') {
        setNeedsEmailVerify(true);
        setAlert({
          message: translateAuthError(
            'email_not_verified',
            'Сначала подтвердите email — введите код из письма ниже или запросите новый.',
          ),
        });
        return;
      }
      setAlert(parseAuthFormAlert(err, 'Ошибка. API на http://127.0.0.1:8000 запущен?'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyFromLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAlert(null);
    const trimmed = verifyCode.replace(/\s/g, '');
    if (trimmed.length < 4) {
      setAlert({ message: 'Введите код из письма' });
      return;
    }
    setLoading(true);
    try {
      const { user, bootstrap } = await api.verifyEmail({ email, code: trimmed });
      if (!isWebAdminEligible(user)) {
        setAlert({
          message:
            'Доступ в админ-панель только для администратора или менеджера. Сотрудники входят в приложение (PWA).',
        });
        api.logout();
        return;
      }
      setNeedsEmailVerify(false);
      onSuccess(user, bootstrap);
    } catch (err: unknown) {
      setAlert(parseAuthFormAlert(err, 'Неверный или просроченный код'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    const target = (forgotEmail || email).trim();
    if (!target) {
      setForgotError('Укажите email');
      return;
    }
    setForgotLoading(true);
    try {
      await api.post('/api/v1/auth/forgot-password', { email: target });
      setForgotSent(true);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 429) {
        setForgotError('Слишком много запросов. Попробуйте через минуту.');
      } else {
        setForgotError(translateAuthApiError(err, 'Не удалось отправить письмо. Попробуйте позже.'));
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResendFromLogin = async () => {
    if (!email.trim()) return;
    setAlert(null);
    setLoading(true);
    try {
      await api.resendVerification(email.trim());
      setAlert(null);
      alert('Если аккаунт существует и не подтверждён, код отправлен повторно.');
    } catch (err: unknown) {
      setAlert(parseAuthFormAlert(err, 'Не удалось отправить письмо'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={authPageShellClass}>
      <div className="mx-auto flex min-h-screen w-full max-w-5xl items-center justify-center">
        <div className={`w-full max-w-sm p-8 ${authCardClass}`}>
          <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--app-primary-soft)] ">
            <BrandMark size={40} />
          </div>
          <h1 className="mb-1 text-center text-2xl font-bold text-[var(--app-text)]">
          Умная смена
          </h1>
          <p className={`mb-6 text-center ${authMutedTextClass}`}>Админ-панель</p>

          {registered && (
            <div className="mb-4 rounded-2xl border border-primary-200 bg-primary-600 px-4 py-3 text-sm text-white ">
              Аккаунт создан. Если настройка не завершилась автоматически, войдите и продолжите её внутри админки.
            </div>
          )}

          {pwaOnlyHint && (
            <div className="mb-4 rounded-2xl border border-sky-200 bg-sky-600 px-4 py-3 text-sm text-white  ">
              Админ-панель для ролей администратора и менеджера. Вход для сотрудников — в мобильном или веб-приложении
              (PWA), а не здесь.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className={authLabelClass}>
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className={authFieldClass}
              />
            </div>
            <div>
              <label htmlFor="password" className={authLabelClass}>
                Пароль
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  className={`${authFieldClass} py-2 pl-3 pr-11`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  aria-pressed={showPassword}
                  className={authIconButtonClass}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {alert ? <AuthFormAlerts {...alert} /> : null}

            {needsEmailVerify && (
              <div className={`space-y-3 ${authPanelClass}`}>
                <form onSubmit={handleVerifyFromLogin} className="space-y-2">
                  <label htmlFor="verifyCode" className={authLabelClass}>
                    Код из письма
                  </label>
                  <input
                    id="verifyCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value)}
                    disabled={loading}
                    className={`${authFieldClass} text-center text-lg tracking-widest`}
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex h-10 w-full items-center justify-center rounded-xl bg-[var(--app-surface-strong)] text-sm font-semibold text-[var(--app-text)]"
                  >
                    Подтвердить и войти
                  </button>
                </form>
                <button
                  type="button"
                  onClick={handleResendFromLogin}
                  disabled={loading}
                  className="w-full text-center text-sm text-primary-600 underline-offset-2 hover:underline"
                >
                  Отправить код снова
                </button>
              </div>
            )}

            {success && (
              <div className="space-y-2 rounded-2xl border border-primary-200 bg-primary-600 px-4 py-3 text-sm text-white ">
                <div>Вход выполнен, перенаправляем в рабочий кабинет…</div>
                <Link href="/" className="font-medium underline">
                  Перейти сейчас
                </Link>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex h-11 w-full items-center justify-center rounded-2xl bg-gradient-to-b from-primary-500 to-primary-600 px-4 text-sm font-semibold text-white shadow-app-sm transition hover:from-primary-600 hover:to-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Входим...
                </>
              ) : (
                <>
                  Войти
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </button>
          </form>

          <div className={`mt-6 space-y-1 text-center text-sm ${authMutedTextClass}`}>
            <p>
              <button
                type="button"
                onClick={() => {
                  setShowForgot((v) => !v);
                  setForgotSent(false);
                  setForgotError('');
                  if (!forgotEmail && email) setForgotEmail(email);
                }}
                className="text-primary-600 hover:underline"
              >
                Забыли пароль?
              </button>
            </p>
            <p>
              <Link href="/register" className="text-primary-600 hover:underline">
                Зарегистрироваться
              </Link>
            </p>
            <p>
              <Link href="/privacy" className="text-primary-600 hover:underline">
                Политика конфиденциальности
              </Link>
            </p>
          </div>

          {showForgot && (
            <div className={`mt-4 space-y-3 ${authPanelClass}`}>
              {forgotSent ? (
                <div className={`space-y-2 text-sm text-[var(--app-text)]`}>
                  <p className="font-medium">Если такой адрес зарегистрирован — письмо со ссылкой отправлено.</p>
                  <p className={authMutedTextClass}>
                    Откройте почту, нажмите кнопку «Сменить пароль» — задайте новый.
                    Ссылка действует ~30 минут и сработает один раз.
                  </p>
                  <button
                    type="button"
                    onClick={() => setForgotSent(false)}
                    className="text-primary-600 hover:underline"
                  >
                    Отправить ещё раз
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotSubmit} className="space-y-2">
                  <label htmlFor="forgotEmail" className={authLabelClass}>
                    Email для восстановления
                  </label>
                  <input
                    id="forgotEmail"
                    type="email"
                    placeholder="name@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    disabled={forgotLoading}
                    className={authFieldClass}
                  />
                  {forgotError && (
                    <div className="text-sm text-rose-600">{forgotError}</div>
                  )}
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="flex h-10 w-full items-center justify-center rounded-xl bg-[var(--app-surface-strong)] text-sm font-semibold text-[var(--app-text)] disabled:opacity-50"
                  >
                    {forgotLoading ? 'Отправляем…' : 'Отправить ссылку для смены пароля'}
                  </button>
                  <p className={`text-xs ${authMutedTextClass}`}>
                    Из соображений приватности мы всегда показываем одинаковый ответ —
                    письмо приходит, только если такой email зарегистрирован.
                  </p>
                </form>
              )}
            </div>
          )}

          {apiOk === false && (
            <p className="mt-3 text-center text-xs text-amber-600">
              API недоступен. Запустите проект через <code className="rounded bg-[var(--app-surface-soft)] px-1">npm run dev</code>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
